import React, { useEffect, useRef } from 'react';
import { X, Bot, Send, Loader2, Mic, Square, Sparkles, Info } from 'lucide-react';

const AIChatPanel = ({
    chatMessages,
    isProcessing,
    isGenerating,
    currentInput,
    setCurrentInput,
    handleSendMessage,
    isRecording,
    isTranscribing,
    toggleRecording,
    // Generation props
    contextFile,
    onContextFileClick,
    onRemoveContextFile,
    includeSearch,
    setIncludeSearch,
    isGenerationMode,
    userSelectedFormat,
    setUserSelectedFormat
}) => {
    const textareaRef = useRef(null);
    const messagesEndRef = useRef(null);

    const prevMessagesLength = useRef(chatMessages?.length || 0);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (chatMessages?.length > prevMessagesLength.current && chatMessages?.length > 2) {
            const lastMsg = chatMessages[chatMessages.length - 1];
            // Don't forcefully scroll down when system starts generating data, keeping the user's current view stable
            const isSystemGenMsg = lastMsg?.type === 'system' && lastMsg?.content?.includes('Generating');

            if (!isSystemGenMsg) {
                scrollToBottom();
            }
        }
        prevMessagesLength.current = chatMessages?.length || 0;
    }, [chatMessages]);

    useEffect(() => {
        if (!currentInput && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [currentInput]);

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50/50">
                {chatMessages.length === 0 ? (
                    isGenerationMode ? (
                        <div className="flex flex-col justify-center h-full px-4 gap-2">
                            <div className="flex items-center gap-2 mb-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                <Info size={16} className="text-blue-500 shrink-0" />
                                <p className="text-xs text-blue-700 font-medium">You can generate up to 500 rows per request.</p>
                            </div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Example prompts</p>
                            {[
                                // { prompt: '50 rows of medical symptom-diagnosis pairs with 5 columns', format: 'Flat_Tabular' },
                                { prompt: 'Sentiment analysis dataset with product reviews, 3 sentiment labels', format: 'Flat_Tabular' },
                                { prompt: 'Chat data: FAQ bot for a SaaS product with 10 Q&A pairs', format: 'chat_array' },
                                { prompt: '200 rows of e-commerce product listings with name, category, price, rating', format: 'Flat_Tabular' },
                                { prompt: '50 rows of instruction-following data with instruction, input, and output columns', format: 'Flat_Tabular' },
                            ].map(({ prompt, format }, i) => (
                                <button
                                    key={i}
                                    onClick={() => { setCurrentInput(prompt); setUserSelectedFormat(format); }}
                                    className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 border border-gray-200 rounded-lg text-xs text-gray-600 hover:text-blue-700 transition-colors"
                                >
                                    &ldquo;{prompt}&rdquo;
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center px-6 text-gray-400">
                            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                                <Bot size={32} className="text-blue-500 opacity-80" />
                            </div>
                            <h3 className="font-medium text-gray-900 mb-1">How can I help?</h3>
                            <p className="text-sm">Ask me to clean, filter, or transform your data.</p>
                            <div className="mt-4 w-full space-y-2 text-left">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Try asking</p>
                                {[
                                    'Remove rows where email is missing',
                                    'Capitalize all names in the customer column',
                                    'Delete duplicate rows'
                                ].map((prompt, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentInput(prompt)}
                                        className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 border border-gray-200 rounded-lg text-xs text-gray-600 hover:text-blue-700 transition-colors"
                                    >
                                        &ldquo;{prompt}&rdquo;
                                    </button>
                                ))}
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        {chatMessages.map((message, index) => (
                            <div key={index} className={`flex flex-col ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-[95%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${message.type === 'user'
                                    ? 'bg-blue-600 text-white rounded-br-none'
                                    : message.type === 'system'
                                        ? 'bg-gray-100 text-gray-700 text-center w-full shadow-none border border-gray-200'
                                        : message.isInvalidRequest
                                            ? 'bg-red-50 text-red-800 border border-red-300 rounded-bl-none'
                                            : message.actions
                                                ? 'bg-white text-gray-800 border border-blue-100 rounded-bl-none shadow-[0_4px_12px_-2px_rgba(37,99,235,0.1)] ring-1 ring-blue-50/50'
                                                : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                                    }`}>
                                    {message.type === 'assistant' && (
                                        <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-blue-600 uppercase tracking-wide">
                                            <Bot size={12} />
                                            <span>Assistant</span>
                                        </div>
                                    )}
                                    {/* Quick Action Header - Skip follow-ups and generate immediately */}
                                    {message.actions && message.actions.some(a => a.label?.includes('Generate Now')) && (
                                        <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                                            {/* <div className="flex items-center gap-1.5 text-xs text-blue-600 font-bold tracking-tight">
                                                 <Sparkles size={14} className="animate-pulse" />
                                                 Direct Link
                                             </div> */}
                                            <button
                                                onClick={() => message.actions.find(a => a.label?.includes('Generate Now'))?.onClick()}
                                                disabled={isGenerating || isProcessing}
                                                className={`px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-[10px] font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                                                    isGenerating || isProcessing
                                                        ? 'opacity-50 cursor-not-allowed'
                                                        : 'hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0'
                                                }`}
                                            >
                                                Skip & Generate Now
                                            </button>
                                        </div>
                                    )}

                                    <div className="whitespace-pre-wrap">
                                        {typeof message.content === 'object' && message.content !== null
                                            ? (message.content.datacreatoraimsg || JSON.stringify(message.content, null, 2))
                                            : message.content}
                                    </div>

                                    {/* Chat Actions */}
                                    {message.actions && (
                                        <div className="flex flex-row gap-1.5 mt-3 pt-2 border-t border-gray-100 flex-wrap">
                                            {message.actions.map((action, idx) => {
                                                if (action.component) {
                                                    const Component = action.component;
                                                    return <Component key={idx} {...action.props} disabled={isGenerating || isProcessing} />;
                                                }

                                                return action.isPaid ? (
                                                    <button
                                                        key={idx}
                                                        onClick={action.onClick}
                                                        disabled={isGenerating || isProcessing}
                                                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-lg transition-all border border-amber-300 shadow-sm ${
                                                            isGenerating || isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                        title="This is a paid custom service"
                                                    >
                                                        {action.label}
                                                        <span className="ml-1 px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold leading-none">Paid</span>
                                                    </button>
                                                ) : action.label?.toLowerCase().includes('generate') ? (
                                                    <button
                                                        key={idx}
                                                        onClick={action.onClick}
                                                        disabled={isGenerating || isProcessing}
                                                        className={`flex-1 relative px-2 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg border border-blue-500 shadow-sm hover:bg-blue-700 transition-all flex items-center justify-center ${
                                                            isGenerating || isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        {action.label}
                                                        {!(isGenerating || isProcessing) && (
                                                            <span
                                                                className="absolute inset-0 rounded-lg"
                                                                style={{ animation: 'generateRing 1.8s ease-in-out infinite', boxShadow: '0 0 0 0 rgba(59,130,246,0.7)' }}
                                                            />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <button
                                                        key={idx}
                                                        onClick={action.onClick}
                                                        disabled={isGenerating || isProcessing}
                                                        className={`px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-all border border-blue-200 whitespace-nowrap shadow-sm hover:shadow ${
                                                            isGenerating || isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        {action.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {(isProcessing || isGenerating) && (
                            <div className="flex justify-start">
                                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-none border border-gray-100 shadow-sm flex items-center gap-3">
                                    <div className="flex gap-1">
                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                    <span className="text-xs font-medium text-gray-500">
                                        {isGenerating ? 'Generating...' : 'Working...'}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100 flex flex-col gap-2">
                {/* Options Toolbar */}
                {isGenerationMode && (
                    <div id="ai-context-search-toolbar" className="flex items-center gap-2">
                        <button
                            onClick={onContextFileClick}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${contextFile ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
                                }`}
                            title="Upload Context File (PDF/CSV)"
                        >
                            <Sparkles size={14} className={contextFile ? "text-blue-600" : "text-gray-400"} />
                            {contextFile ? <span className="max-w-[120px] truncate">{contextFile.name}</span> : <span>Add Context Through Files</span>}
                        </button>
                        {contextFile && (
                            <button onClick={onRemoveContextFile} className="text-gray-400 hover:text-red-500 p-1 hover:bg-red-50 rounded">
                                <X size={14} />
                            </button>
                        )}

                        <button
                            onClick={() => {
                                if (!includeSearch) {
                                    // Turning ON web search — clear context file
                                    onRemoveContextFile();
                                }
                                setIncludeSearch(!includeSearch);
                            }}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${includeSearch ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
                                }`}
                            title="Toggle Web Search for Generation"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></svg>
                            <span>Web Search {includeSearch ? 'On' : 'Off'}</span>
                        </button>
                    </div>
                )}
                {isGenerationMode && (
                    <div id="ai-format-selector" className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
                        <span className="text-xs font-medium text-gray-400 whitespace-nowrap flex items-center gap-1" title="Choose format to generate: Tabular for Excel/CSV format, Chat Data for conversational format">
                            Format <Info size={12} className="cursor-help" />:
                        </span>
                        <button
                            onClick={() => setUserSelectedFormat('Flat_Tabular')}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${userSelectedFormat === 'Flat_Tabular' ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-transparent'}`}
                            title="Generate data in rows and columns format (like CSV or Excel)"
                        >
                            Structured
                        </button>
                        <button
                            onClick={() => setUserSelectedFormat('chat_array')}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${userSelectedFormat === 'chat_array' ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-transparent'}`}
                            title="Generate data as conversational exchanges between a user and assistant"
                        >
                            Chat Data
                        </button>
                    </div>
                )}

                {(contextFile || includeSearch) && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500 shrink-0"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                        <span className="text-xs text-amber-700">
                            {contextFile && includeSearch
                                ? 'Context file + web search active — generation may take longer than usual.'
                                : contextFile
                                    ? 'Context file attached — generation may take a bit longer.'
                                    : 'Web search enabled — generation may take longer than usual.'}
                        </span>
                    </div>
                )}

                <div className="relative">
                    <textarea
                        ref={textareaRef}
                        value={currentInput}
                        onChange={(e) => setCurrentInput(e.target.value)}
                        placeholder={isRecording ? "Listening..." : "Make changes to the dataset..."}
                        className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm resize-none pr-10 max-h-[400px] overflow-y-auto ${isRecording ? 'bg-blue-50/50' : ''}`}
                        rows="1"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (!(isProcessing || isGenerating) && currentInput.trim()) {
                                    handleSendMessage();
                                }
                            }
                        }}
                        disabled={isProcessing || isGenerating}
                        readOnly={isRecording || isTranscribing}
                        style={{ height: 'auto', minHeight: '44px', maxHeight: '400px' }}
                        id="ai-chat-input"
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 400) + 'px';
                        }}
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={isProcessing || isGenerating || !currentInput.trim()}
                        className={`absolute right-3 bottom-3 p-2 rounded-lg transition-all duration-200 ${!currentInput.trim() || isProcessing || isGenerating
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                            }`}
                    >
                        {isProcessing || isGenerating ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    </button>
                    <button
                        onClick={toggleRecording}
                        disabled={isProcessing || isGenerating || isTranscribing}
                        className={`absolute right-14 bottom-3 p-2 rounded-lg transition-all duration-200 ${isTranscribing
                            ? 'bg-blue-100 text-blue-600'
                            : isRecording
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                        title={isTranscribing ? "Transcribing..." : isRecording ? "Stop Recording" : "Start Voice Input"}
                    >
                        {isTranscribing ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : isRecording ? (
                            <div className="relative">
                                <Square size={20} className="relative z-10" />
                                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                </span>
                            </div>
                        ) : (
                            <Mic size={20} />
                        )}
                    </button>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
                    <span>
                        {isGenerationMode ? "Max 500 rows per generation." : "Press Enter to send, Shift+Enter for new line"}
                    </span>
                    {!isGenerationMode && (
                        <span className="text-blue-600/60 font-medium flex items-center gap-1">
                            <Info size={12} /> Max 500 points/edit
                        </span>
                    )}
                </div>
                <p className="text-[10px] text-gray-600 text-center px-1 mt-1 mb-1.5 tracking-wide opacity-90">
                    AI-generated (synthetic) data. Please review before real-world or production use.
                </p>
                <style>{`
                    @keyframes generatePulse {
                        0%, 100% { background-color: rgb(37,99,235); transform: scale(1); }
                        50% { background-color: rgb(29,78,216); transform: scale(1.04); }
                    }
                    @keyframes generateRing {
                        0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.6); }
                        70% { box-shadow: 0 0 0 6px rgba(59,130,246,0); }
                        100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
                    }
                    .animate-in {
                        animation: fadeIn 0.4s ease-out forwards;
                    }
                    .slide-in-from-bottom-4 {
                        animation: slideInBottom 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideInBottom {
                        from { transform: translateY(1.5rem); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }
                    .no-scrollbar::-webkit-scrollbar {
                        display: none;
                    }
                    .no-scrollbar {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default AIChatPanel;
